/**
 * Human-readable labels for module keys in audit log details.
 */
const MODULE_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  tariff_calculator: "Tariff Calculator",
  tariff_chat: "Tariff Chat",
  risk_prediction: "Risk Prediction",
  route_optimization: "Route Optimization",
  admin_panel: "Admin Panel",
};

function toReadableLabel(raw: string): string {
  return raw.replaceAll("_", " ");
}

function capitalizeWords(raw: string): string {
  return raw.replaceAll(/\b\w/g, (c) => c.toUpperCase());
}

function formatModuleKey(key: string): string {
  const label = MODULE_LABELS[key];
  if (label) return label;
  return capitalizeWords(toReadableLabel(key));
}

export type AuditEntryForFormat = {
  action: string;
  details?: Record<string, unknown> | null;
  target_id?: string | null;
  target_type?: string | null;
};

type FormatOptions = {
  targetId?: string | null;
  targetType?: string | null;
  targetLabel?: string | null;
};

function stringifyAuditField(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return "[unserializable object]";
  }
}

function formatRoleUpdate(details: Record<string, unknown>, options?: FormatOptions): string | null {
  const oldRole = details.old_role as string | undefined;
  const newRole = details.new_role as string | undefined;
  const who = (details.target_user_label as string) ?? options?.targetLabel ?? null;
  const oldLabel = oldRole ? toReadableLabel(String(oldRole)) : "";
  const newLabel = newRole ? toReadableLabel(String(newRole)) : "";

  if (oldLabel && newLabel) {
    const whoPrefix = who ? `${who}'s role changed from ` : "Role changed from ";
    return `${whoPrefix}${oldLabel} to ${newLabel}`;
  }

  if (newLabel) {
    return who ? `${who}'s role set to ${newLabel}` : `Role set to ${newLabel}`;
  }

  return null;
}

function formatSetRoleModules(details: Record<string, unknown>, options?: FormatOptions): string {
  const role = (details.role as string) ?? options?.targetId ?? "role";
  const roleLabel = toReadableLabel(String(role));
  const enabled = details.enabled as string[] | undefined;
  const disabled = details.disabled as string[] | undefined;
  const parts: string[] = [];

  if (enabled?.length) {
    parts.push(`Enabled: ${enabled.map(formatModuleKey).join(", ")}`);
  }
  if (disabled?.length) {
    parts.push(`Disabled: ${disabled.map(formatModuleKey).join(", ")}`);
  }

  if (parts.length) {
    return `Default permissions for ${roleLabel} updated. ${parts.join(". ")}`;
  }
  return `Default permissions for ${roleLabel} updated`;
}

function formatSetModulePermissions(details: Record<string, unknown>): string {
  const enabled = details.enabled as string[] | undefined;
  const disabled = details.disabled as string[] | undefined;
  const parts: string[] = [];

  if (enabled?.length) {
    parts.push(`Enabled: ${enabled.map(formatModuleKey).join(", ")}`);
  }
  if (disabled?.length) {
    parts.push(`Disabled: ${disabled.map(formatModuleKey).join(", ")}`);
  }

  if (parts.length) {
    return `User module access updated. ${parts.join(". ")}`;
  }
  return "User module access updated";
}

function formatTeamAction(action: string, details: Record<string, unknown>): string | null {
  const name = stringifyAuditField(details.name);
  const user = stringifyAuditField(details.user);

  switch (action) {
    case "create_team":
      return name ? `Team created: ${name}` : "Team created";
    case "update_team":
      return name ? `Team name: ${name}` : "Team updated";
    case "delete_team":
      return "Team deleted";
    case "add_team_member":
      return user ? `Member added: ${user}` : "Member added to team";
    case "remove_team_member":
      return user ? `Member removed: ${user}` : "Member removed from team";
    default:
      return null;
  }
}

function formatWorkspaceAction(action: string, details: Record<string, unknown>): string | null {
  const name = stringifyAuditField(details.name);

  switch (action) {
    case "create_workspace":
      return name ? `Workspace created: ${name}` : "Workspace created";
    case "update_workspace":
      return name ? `Workspace name: ${name}` : "Workspace updated";
    case "delete_workspace":
      return "Workspace deleted";
    case "assign_workspace_team":
      return "Team assigned to workspace";
    case "revoke_workspace_team":
      return "Team removed from workspace";
    default:
      return null;
  }
}

function formatWorkspaceResourceAction(action: string, details: Record<string, unknown>): string | null {
  const resourceName = stringifyAuditField(details.resource_name);

  switch (action) {
    case "add_workspace_resource":
      return resourceName ? `Resource added: ${resourceName}` : "Resource added to workspace";
    case "remove_workspace_resource":
      return "Resource removed from workspace";
    default:
      return null;
  }
}

function formatSimpleAction(action: string): string | null {
  if (action === "deactivate_user") {
    return "User deactivated";
  }
  return null;
}

function formatFallbackDetails(details: Record<string, unknown>): string {
  // Fallback: format key-value pairs readably (exclude raw modules object if we have enabled/disabled)
  const skipKeys = new Set(["modules", "clerk_user_id"]);

  const entries = Object.entries(details).filter(([key, value]) => {
    if (skipKeys.has(key)) {
      return false;
    }
    if (value === null || value === undefined || value === "") {
      return false;
    }
    return true;
  });

  if (entries.length === 0) return "—";

  if (entries.length === 1) {
    const [key, value] = entries[0];
    const label = toReadableLabel(key);
    const formattedValue = stringifyAuditField(value);
    return `${label}: ${formattedValue}`;
  }

  return entries
    .map(([key, value]) => {
      const label = toReadableLabel(key);
      const formattedValue = stringifyAuditField(value);
      return `${label}: ${formattedValue}`;
    })
    .join(". ");
}

/**
 * Format audit log details into a clear, human-readable summary.
 */
export function formatAuditDetails(
  action: string,
  details: unknown,
  options?: FormatOptions,
): string {
  if (!details || typeof details !== "object") return "—";

  const normalizedDetails = details as Record<string, unknown>;

  const roleUpdate = formatRoleUpdate(normalizedDetails, options);
  if (roleUpdate) return roleUpdate;

  if (action === "set_role_modules") {
    return formatSetRoleModules(normalizedDetails, options);
  }

  if (action === "set_module_permissions") {
    return formatSetModulePermissions(normalizedDetails);
  }

  const teamAction = formatTeamAction(action, normalizedDetails);
  if (teamAction) return teamAction;

  const workspaceAction = formatWorkspaceAction(action, normalizedDetails);
  if (workspaceAction) return workspaceAction;

  const workspaceResourceAction = formatWorkspaceResourceAction(action, normalizedDetails);
  if (workspaceResourceAction) return workspaceResourceAction;

  const simpleAction = formatSimpleAction(action);
  if (simpleAction) return simpleAction;

  return formatFallbackDetails(normalizedDetails);
}

